import { useState, type SubmitEvent } from "react";
import { useTranslation } from "react-i18next";
import { importFromCloud, type UpdatePolicy } from "../../../api/content/hostingApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { isProblemWithCode, toUiError } from "../../../errors/uiError";
import { useSubmission } from "../../../shared/state/useSubmission";
import ChoiceCard from "../../../ui/forms/ChoiceCard";
import Field from "../../../ui/forms/Field";
import FieldGroup from "../../../ui/forms/FieldGroup";
import { FORM_CONTROL_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import { requestSourceUpdate } from "../connections/sourceConnectionsApi";
import { openTargetCorpus } from "./corpusImport";
import CorpusTargetFields from "./CorpusTargetFields";
import HelpArticleLink from "../../help/HelpArticleLink";
import ImportSubmit, { type ImportBlocker } from "./ImportSubmit";
import ImportWorkflowField from "./ImportWorkflowField";
import { importConnection, repositoryBlocker, useRepositoryStep } from "./privateRepository";
import { RepositoryStepNotice, RepositoryStepResolve } from "./PrivateRepositoryStep";
import SourceImportSummary, { type SourceImportOutcome } from "./SourceImportSummary";
import { useCorpusTarget, useImportWorkflow, workflowBlocker, type ImportFormProps } from "./importSetup";

const POLICIES: readonly UpdatePolicy[] = ["automatic", "on_demand"];
/** How often DMW looks for new commits; `""` is only when someone asks. */
const INTERVALS = { "": null, hourly: 3600, daily: 86_400, weekly: 604_800 } as const;
type IntervalChoice = keyof typeof INTERVALS;

/**
 * Cloud (web-app.md WA-9, F16): the repository, the corpus and the import Workflow; one
 * action creates the hosted source, its mirror and the connection (content.md D-18), then
 * asks the connection for the import of the fetched head, which runs the Workflow in a Thread. A private repository is read
 * through the person's own connection to its host (ADR-0035): the form checks the address first and asks for what is missing.
 */
export default function CloudImportForm({ projectId, corpusId, onStarted }: ImportFormProps) {
  const { t } = useTranslation();
  const corpus = useCorpusTarget(projectId, corpusId, "fromInput");
  const update = useImportWorkflow(projectId);
  const [remote, setRemote] = useState("");
  const [policy, setPolicy] = useState<UpdatePolicy>("automatic");
  const [branch, setBranch] = useState("main");
  // Daily by default: with "only when asked", an automatic connection would fetch once and then never again.
  const [fetchInterval, setFetchInterval] = useState<IntervalChoice>("daily");
  const [outcome, setOutcome] = useState<SourceImportOutcome | null>(null);
  const { state, submit } = useSubmission();
  const repository = useRepositoryStep(remote.trim());
  const checking = useSubmission();

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    const target = corpus.target();
    const workflow = update.workflow?.reference ?? null;
    if (target === null || workflow === null) return;
    setOutcome(null);
    submit(async () => {
      const checked = await repository.check(remote.trim(), { fresh: true });
      // A private repository waits until its connection, the App and the consent are there.
      if (repositoryBlocker(checked, repository.consented) !== null) return;
      try {
        const opened = await openTargetCorpus(target);
        corpus.imported(opened.corpusId, "fromInput");
        const source = await importFromCloud(projectId, { remote: remote.trim(), corpusId: opened.corpusId, policy, branch: branch.trim(), intervalSeconds: INTERVALS[fetchInterval], workflow, externalConnectionId: importConnection(checked) });
        let firstFetch: "fetched" | "unchanged" | "failed";
        try {
          // Module B's update request fetches the repository and asks for the import of its head, under either policy:
          // connecting and running the import asks for this first commit.
          await requestSourceUpdate(source);
          firstFetch = "fetched";
        } catch (caught) {
          // Its only refusal for a connection just made: the branch has no commit yet.
          firstFetch = isProblemWithCode(toUiError(caught), "invalid_value") ? "unchanged" : "failed";
        }
        setOutcome({ kind: "cloud", corpusName: opened.name, source, firstFetch });
        setRemote("");
      } finally {
        corpus.reloadCorpora();
      }
    });
  };
  const blocker: ImportBlocker | null = remote.trim() === "" ? "enterRemote" : repositoryBlocker(repository.current, repository.consented) ?? workflowBlocker(update) ?? (corpus.nameMissing ? "nameCorpus" : null);

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <fieldset disabled={state.status === "submitting"} className="space-y-4">
        <Field label={t("sources.import.cloud.remote")} hint={t("sources.import.cloud.remoteHint")} actions={<HelpArticleLink article="privateRepository" />}>
          {(control) => <input {...control} required className={FORM_CONTROL_CLASS} placeholder="https://example.org/team/letters.git" value={remote} onChange={(event) => { setRemote(event.target.value); }}
            onBlur={() => { const address = remote.trim(); if (address !== "") checking.submit(async () => { await repository.check(address); }); }} />}
        </Field>
        {checking.state.status === "submitting" ? <p role="status" className="app-help-text">{t("sources.import.cloud.private.checking")}</p> : null}
        {checking.state.status === "failed" ? <ErrorNotice error={checking.state.error} /> : null}
        <RepositoryStepNotice step={repository} />
        <CorpusTargetFields {...corpus.fields} jsonLines={false} />
        <ImportWorkflowField projectId={projectId} choice={update} />
        <FieldGroup legend={t("sources.import.cloud.policy")} className="grid gap-2 sm:grid-cols-2">
          {POLICIES.map((option) => (
            <ChoiceCard key={option} name="cloud-policy" checked={policy === option} onChoose={() => { setPolicy(option); }}
              title={t(`sources.import.cloud.policies.${option}`)} description={t(`sources.import.cloud.policyHints.${option}`)} />
          ))}
        </FieldGroup>
        <details className="space-y-3">
          <summary className="app-label cursor-pointer list-item">{t("sources.import.advanced")}</summary>
          <Field label={t("sources.import.cloud.branch")}>
            {(control) => <input {...control} required className={FORM_CONTROL_CLASS} value={branch} onChange={(event) => { setBranch(event.target.value); }} />}
          </Field>
          <Field label={t("sources.import.cloud.interval")}>
            {(control) => (
              <select {...control} className={FORM_SELECT_CLASS} value={fetchInterval} onChange={(event) => { setFetchInterval((Object.keys(INTERVALS) as IntervalChoice[]).find((key) => key === event.target.value) ?? ""); }}>
                {(Object.keys(INTERVALS) as IntervalChoice[]).map((key) => <option key={key} value={key}>{t(`sources.import.cloud.intervals.${key === "" ? "asked" : key}`)}</option>)}
              </select>
            )}
          </Field>
        </details>
      </fieldset>
      {state.status === "submitting" ? <p role="status" className="app-help-text">{t("sources.import.cloud.working")}</p> : null}
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      {outcome === null ? null : <SourceImportSummary projectId={projectId} outcome={outcome} onStarted={onStarted} />}
      <ImportSubmit label={t("sources.import.cloud.submit")} blocker={blocker} submitting={state.status === "submitting"}
        resolve={<RepositoryStepResolve step={repository} onCheckAgain={() => { checking.submit(async () => { await repository.check(remote.trim(), { fresh: true }); }); }} />} />
    </form>
  );
}
